/* eslint-disable react/prop-types */
import React, { memo, useCallback, useEffect, useRef, useState } from "react";
import { Outlet } from "react-router-dom";
import { CanvasWidget } from "@projectstorm/react-canvas-core";
import "react-toastify/dist/ReactToastify.css";
import SideMenu from "./Components/sidebar/SideMenu";
import WorkflowOverview from "./WorkflowOverview";

const { isSuperadmin } = JSON.parse(
  document.querySelector("#app").dataset.page
).props;

function MainWindow({
  addChild,
  engine,
  redistribute,
  zoomToFit,
  saveProject,
  zoomToSelected,
  resetLayout,
}) {
  const [animatd, setAnimatd] = useState(false); // animation during zoom
  const [zoomLevel, setZoomLevel] = useState(() =>
    Math.round(engine.model.getZoomLevel())
  );
  const [showZoomIndicator, setShowZoomIndicator] = useState(false);
  const [showWorkflowOverview, setShowWorkflowOverview] = useState(false);
  const overviewHideTimeout = useRef();
  const refreshDiagramGeometry = useCallback(() => {
    const canvas = engine?.getCanvas?.();

    if (!canvas?.offsetWidth || !canvas?.offsetHeight) {
      return;
    }

    const model = engine?.getModel?.() || engine?.model;

    (model?.getNodes?.() || []).forEach((node) => {
      Object.values(node.getPorts?.() || {}).forEach((port) => {
        if (port?.reportedPosition) {
          port.reportPosition?.();
        }
      });
    });

    engine?.repaintCanvas?.();
  }, [engine]);

  useEffect(() => {
    let isCancelled = false;
    const refresh = () => {
      if (!isCancelled) {
        refreshDiagramGeometry();
      }
    };

    const firstFrame = requestAnimationFrame(() => {
      refresh();
      requestAnimationFrame(refresh);
    });
    const delayedRefresh = setTimeout(refresh, 150);

    return () => {
      isCancelled = true;
      cancelAnimationFrame(firstFrame);
      clearTimeout(delayedRefresh);
    };
  }, [refreshDiagramGeometry]);

  useEffect(() => {
    let hideZoomIndicatorTimeout;

    setZoomLevel(Math.round(engine.model.getZoomLevel()));
    const listener = engine.model.registerListener({
      zoomUpdated: (event) => {
        setZoomLevel(Math.round(event.zoom));
        setShowZoomIndicator(true);
        clearTimeout(hideZoomIndicatorTimeout);
        hideZoomIndicatorTimeout = setTimeout(() => {
          setShowZoomIndicator(false);
        }, 2000);
      },
    });

    return () => {
      listener?.deregister?.();
      clearTimeout(hideZoomIndicatorTimeout);
    };
  }, [engine]);

  useEffect(
    () => () => {
      clearTimeout(overviewHideTimeout.current);
    },
    []
  );

  const showOverview = useCallback((duration = 2000) => {
    setShowWorkflowOverview(true);
    clearTimeout(overviewHideTimeout.current);
    overviewHideTimeout.current = setTimeout(() => {
      setShowWorkflowOverview(false);
    }, duration);
  }, []);

  const handleCanvasDoubleClick = useCallback(() => {
    setAnimatd(true);
    setTimeout(() => {
      setAnimatd(false);
    }, 300);
    zoomToSelected();
  }, [zoomToSelected]);

  return (
    <div
      className="main-window position-relative mb-n5"
      style={{
        height: isSuperadmin ? "calc(100vh - 160px)" : "calc(100vh - 140px)",
        display: "flex",
      }}
    >
      <SideMenu
        addChild={addChild}
        redistribute={redistribute}
        saveProject={saveProject}
        zoomToFit={zoomToFit}
        zoomToSelected={zoomToSelected}
        resetLayout={resetLayout}
      />
      <div
        style={{ flexGrow: 1, position: "relative" }}
        onDoubleClick={handleCanvasDoubleClick}
        onMouseMove={(event) => {
          if (event.buttons) {
            showOverview();
          }
        }}
      >
        <CanvasWidget
          className={`bg-loop bg-accross diagram-container ${
            animatd ? " animated" : ""
          } `}
          engine={engine}
        />
        {showZoomIndicator && (
          <output className="workflow-zoom-indicator" aria-label="Canvas zoom level">
            {zoomLevel}%
          </output>
        )}
        {showWorkflowOverview && (
          <WorkflowOverview engine={engine} onInteract={() => showOverview(4000)} />
        )}
      </div>
      <div id="sidebar" style={{ overflow: "auto", position: "relative" }} />
      <Outlet />
    </div>
  );
}

export default memo(MainWindow);
